import { useEffect, useRef, useState } from "react";

const COPY = {
  en: {
    button: "Operator guide",
    close: "Close",
    description: "What to check and what the common terms mean.",
    glossary: "Glossary",
    investigate: "Safe investigation",
    investigateText:
      "Confirm the player, amount, currency and reference. Use Audit to establish who did what. Sensitive money changes require a reason and a second operator.",
    start: "Start with the dashboard",
    steps: [
      "Check “Needs attention” for stale rounds, approvals and integrity issues.",
      "Use Live games to spot activity that may be stuck.",
      "Open the relevant Explorer before taking action.",
    ],
    terms: [
      ["Stake", "Money committed to a game or hand."],
      ["Payout", "Money returned to the player after settlement."],
      ["GGR", "Settled stakes minus player returns; not accounting profit."],
      ["Ledger", "The append-only record of every money movement."],
      ["Round", "One complete game lifecycle, from opening through settlement or void."],
      ["Integrity", "Evidence that game history and audit records have not been rewritten."],
      ["Approval", "A sensitive action waiting for a different operator to decide."],
    ],
    title: "Operator guide",
  },
  fr: {
    button: "Guide opérateur",
    close: "Fermer",
    description: "Les contrôles à effectuer et la signification des termes courants.",
    glossary: "Glossaire",
    investigate: "Investigation sécurisée",
    investigateText:
      "Vérifiez le joueur, le montant, la devise et la référence. Utilisez l’audit pour savoir qui a fait quoi. Les modifications financières sensibles exigent un motif et un second opérateur.",
    start: "Commencez par le tableau de bord",
    steps: [
      "Consultez « À vérifier » pour les manches bloquées, approbations et problèmes d’intégrité.",
      "Utilisez les jeux en direct pour repérer une activité potentiellement bloquée.",
      "Ouvrez l’explorateur concerné avant d’agir.",
    ],
    terms: [
      ["Mise", "Somme engagée dans un jeu ou une main."],
      ["Paiement", "Somme retournée au joueur après le règlement."],
      ["PBJ", "Mises réglées moins retours joueurs ; ce n’est pas le bénéfice comptable."],
      ["Grand livre", "Registre immuable de chaque mouvement financier."],
      ["Manche", "Cycle complet d’un jeu, de l’ouverture au règlement ou à l’annulation."],
      ["Intégrité", "Preuve que l’historique du jeu et l’audit n’ont pas été réécrits."],
      ["Approbation", "Action sensible en attente de la décision d’un autre opérateur."],
    ],
    title: "Guide opérateur",
  },
} as const;

export function OperatorGuide({ language = "en" }: { language?: "en" | "fr" }) {
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const copy = COPY[language];

  useEffect(() => {
    if (!open) return;
    if (dialog.current?.showModal && !dialog.current.open) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");
  }, [open]);

  function close() {
    dialog.current?.close?.();
    setOpen(false);
  }

  return (
    <>
      <button
        aria-label={copy.button}
        className="guide-button"
        onClick={() => setOpen(true)}
        type="button"
      >
        <span aria-hidden="true">?</span>
        <span>{copy.button}</span>
      </button>
      {open ? (
        <dialog
          aria-labelledby="operator-guide-title"
          className="guide-dialog"
          onCancel={close}
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") close();
          }}
          ref={dialog}
        >
          <div className="dialog-head guide-head">
            <div className="guide-head-copy">
              <h2 id="operator-guide-title">{copy.title}</h2>
              <p>{copy.description}</p>
            </div>
            <button className="btn btn-quiet btn-sm" onClick={close} type="button">
              {copy.close}
            </button>
          </div>
          <div className="guide-body">
            <section>
              <h3>{copy.start}</h3>
              <ol>
                {copy.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
            <section>
              <h3>{copy.investigate}</h3>
              <p>{copy.investigateText}</p>
            </section>
            <section>
              <h3>{copy.glossary}</h3>
              <dl className="guide-glossary">
                {copy.terms.map(([term, definition]) => (
                  <div key={term}>
                    <dt>{term}</dt>
                    <dd>{definition}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>
        </dialog>
      ) : null}
    </>
  );
}
